import React from "react";

import { Truncated } from "~/components/truncated";
import { assertNever } from "~/tools/assert-never";
import { cx } from "~/tools/class-variance";
import { toDate } from "~/tools/date-time";
import { pluralize } from "~/tools/pluralize";
import type { Point } from "~/tools/point";
import type { Size } from "~/tools/size";

import { useOpenInRightPane } from "../../../app/state/right-pane/hooks";
import { SpecificRange } from "../../../app/state/view/timeline/TimeRange";
import { GenericErrorMessage } from "../../react/error/GenericErrorMessage";
import { LoadingSpinner } from "../../react/loading-spinner/LoadingSpinner";
import { PortalToBody } from "../../react/portal/Portal";

import {
	TIMELINE_EVENT_CATEGORY_PROPERTIES,
	type TimelineEventBucket,
	type TimelineEventCategory,
	getTimelineEventBucketEndTime,
	getTimelineEventBucketStartTime,
} from "./domain";
import type { TimelineHoveredEventBucket } from "./TimelineEventBucketsState";
import { usePopoverBucket } from "./usePopoverBucket";
import { useQueryEventTypesInBucket } from "./useQueryEventTypesInBucket";

export type TimelineEventBucketsPopoverPlacement = "top" | "bottom-left";

const placementToStyles = ({
	placement,
	host,
	position,
	bucketSize,
	offset,
}: {
	placement: TimelineEventBucketsPopoverPlacement;
	host: HTMLElement;
	position: Point;
	bucketSize: Size;
	offset: Point;
}): React.CSSProperties => {
	switch (placement) {
		case "bottom-left": {
			return {
				right: host.clientWidth - position.x - offset.x,
				top: position.y + bucketSize.height + offset.y,
			};
		}

		case "top": {
			return {
				left: position.x + bucketSize.width / 2 + offset.x,
				bottom: host.clientHeight - position.y - offset.y,
				transform: "translate(-50%, 0)",
			};
		}

		default: {
			return assertNever(placement);
		}
	}
};

export interface TimelineEventBucketsPopoverOptions<TBucket> {
	/**
	 * @default 0
	 */
	offset?: number | Point;

	/**
	 * @default "top"
	 */
	placement?: TimelineEventBucketsPopoverPlacement;

	/**
	 * @default result of useQueryToExecute()
	 */
	topologyQuery?: string;
	overflowCount?: number;

	onItemClick?(bucket: TBucket, eventCategory: TimelineEventCategory, eventType?: string): void;
}

export interface TimelineEventBucketsPopoverProps extends TimelineEventBucketsPopoverOptions<TimelineEventBucket> {
	bucketSize: Size;
	eventCategory: TimelineEventCategory;
}

export const TimelineEventBucketsPopover: React.FC<TimelineEventBucketsPopoverProps> = ({
	eventCategory,
	...props
}) => {
	const [popoverBucket, hoverPopoverBucket] = usePopoverBucket();

	/**
	 * there is a possibility that different TimelineEventBuckets being used in the Timeline
	 * have buckets starting from the same Time, so to avoid showing multiple Popups each TimelineEventBuckets
	 * popover is visible only for associated eventCategory
	 */
	if (eventCategory !== popoverBucket?.eventCategory) {
		return null;
	}

	return (
		<PortalToBody>
			<TimelineBucketPopoverContainer
				{...popoverBucket}
				{...props}
				eventCategory={eventCategory}
				onHover={hoverPopoverBucket}
			/>
		</PortalToBody>
	);
};

interface TimelineBucketPopoverContainerProps
	extends TimelineEventBucketsPopoverOptions<TimelineEventBucket>,
		TimelineHoveredEventBucket {
	eventCategory: TimelineEventCategory;
	bucketSize: Size;
	onHover: React.Dispatch<boolean>;
}

const TimelineBucketPopoverContainer: React.FC<TimelineBucketPopoverContainerProps> = ({
	placement = "top",
	offset = 0,
	topologyQuery,
	eventCategory,
	bucket,
	bucketSize,
	overflowCount = bucket.count,
	position,
	onHover,
	onItemClick,
}) => {
	const bucketStartTime = getTimelineEventBucketStartTime(bucket);
	const bucketEndTime = getTimelineEventBucketEndTime(bucket);
	const bucketTimeRange = React.useMemo(
		() => new SpecificRange(bucketStartTime, bucketEndTime),
		[bucketStartTime, bucketEndTime],
	);

	const openInRightPane = useOpenInRightPane();
	const { isLoading, error, data } = useQueryEventTypesInBucket({
		topologyQuery,
		eventCategories: eventCategory === "*" ? undefined : [eventCategory],
		timeRange: bucketTimeRange,
	});

	const onItemClickHandler = (eventType?: string) => {
		if (onItemClick == null) {
			openInRightPane("events", toDate(bucketStartTime), toDate(bucketEndTime ?? Date.now()), eventType);
		} else {
			onItemClick(bucket, eventCategory, eventType);
		}
	};

	return (
		<div
			className="fixed z-50 min-w-[160px] max-w-[300px] rounded-[3px] bg-black/80 p-2 text-white text-xs"
			onMouseEnter={() => onHover(true)}
			onMouseLeave={() => onHover(false)}
			style={placementToStyles({
				placement,
				host: document.body,
				position,
				bucketSize,
				offset: typeof offset === "number" ? { x: offset, y: offset } : offset,
			})}
		>
			{isLoading && <LoadingSpinner size="small" />}

			{error && <GenericErrorMessage color="light" error={error} />}

			{data?.types.map((eventType) => (
				<PopoverItem key={eventType} onClick={() => onItemClickHandler(eventType)} type="button">
					<Truncated>{`${eventType} events`}</Truncated>
				</PopoverItem>
			))}

			{data?.overflow ? <OverflowNote overflow={data.overflow} /> : null}

			{data != null && (data.types.length > 0 || Boolean(data.overflow)) && (
				<hr className="my-2 border-solid text-gray-700" />
			)}

			<PopoverItem className="font-medium" onClick={() => onItemClickHandler()} type="button">
				{TIMELINE_EVENT_CATEGORY_PROPERTIES[eventCategory].label} (
				{bucket.count > overflowCount ? `${overflowCount}+` : bucket.count})
			</PopoverItem>
		</div>
	);
};

const PopoverItem: React.FC<React.ComponentProps<"button">> = ({ className, ...props }) => (
	<button
		{...props}
		className={cx(
			"block h-6 w-full cursor-pointer rounded-[3px] border-none bg-transparent px-2 text-left hover:bg-primary-500",
			className,
		)}
	/>
);

const pluralizeTypes = pluralize({
	1: "type",
	more: "types",
});

const OverflowNote: React.FC<{
	overflow: number;
}> = ({ overflow }) => (
	<div className="text-gray-600">{`...and ${overflow > 99 ? "99+" : overflow} other event ${pluralizeTypes(
		overflow,
	)}`}</div>
);
